// src/services/ApiService.ts
import { apiConfig, buildApiUrl } from "../config/apiConfig";

// Typed HTTP error so downstream `.status` / `.data` / `.headers` checks keep working
export class HttpError extends Error {
  status: number;
  data: any;
  headers: Headers | Record<string, string>;
  constructor(status: number, data: any, headers: any, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = "HttpError";
    this.status = status;
    this.data = data;
    this.headers = headers;
  }
}

// Read the HTTP status from a thrown error, if any (undefined for network/timeout errors)
export function getStatus(error: any): number | undefined {
  return (error as HttpError | undefined)?.status;
}

// Read a header value from either a Headers instance or a plain object
export function getHeader(
  headers: Headers | Record<string, string> | undefined,
  name: string
): string | null {
  if (!headers) return null;
  if (typeof (headers as Headers).get === "function") {
    return (headers as Headers).get(name);
  }
  const record = headers as Record<string, string>;
  return record[name] ?? record[name.toLowerCase()] ?? null;
}

// Minimal request config (replaces AxiosRequestConfig). Index signature keeps
// axios-era fields such as `onUploadProgress` accepted by call sites.
export interface HttpRequestConfig {
  method?: string;
  url: string;
  data?: any;
  params?: Record<string, any>;
  headers?: Record<string, string>;
  timeout?: number;
  signal?: AbortSignal;
  // When set, httpFetch uploads via XMLHttpRequest (fetch cannot report
  // request-body progress) and fires this on each upload progress tick.
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void;
  [key: string]: any;
}

// Minimal response shape (replaces AxiosResponse) - callers read `.data`
export interface HttpResponse<T = any> {
  data: T;
  status: number;
  headers: Headers;
}

const hasHeader = (headers: Record<string, string>, name: string): boolean =>
  Object.keys(headers).some((k) => k.toLowerCase() === name.toLowerCase());

const parseResponseBody = async (res: Response): Promise<any> => {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

// Build a `Headers` from an XHR's raw response header string.
const xhrResponseHeaders = (xhr: XMLHttpRequest): Headers => {
  const h = new Headers();
  const raw = xhr.getAllResponseHeaders?.() || "";
  for (const line of raw.trim().split(/[\r\n]+/)) {
    const i = line.indexOf(":");
    if (i > 0) {
      try {
        h.append(line.slice(0, i).trim(), line.slice(i + 1).trim());
      } catch {
        /* skip forbidden/duplicate header */
      }
    }
  }
  return h;
};

const parseXhrBody = (xhr: XMLHttpRequest): any => {
  const text = xhr.responseText;
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

// XMLHttpRequest transport — used only when the caller needs upload progress
// (e.g. the zip upload). Mirrors httpFetch's contract exactly: resolves to an
// HttpResponse, throws HttpError on non-2xx, and normalizes timeout/abort to the
// same error shapes the fetch path produces (ECONNABORTED / AbortError).
function xhrFetch<T>(
  method: string,
  url: string,
  headers: Record<string, string>,
  body: BodyInit | undefined,
  timeout: number | undefined,
  signal: AbortSignal | undefined,
  onUploadProgress: (progressEvent: { loaded: number; total?: number }) => void,
): Promise<HttpResponse<T>> {
  return new Promise<HttpResponse<T>>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    for (const [key, value] of Object.entries(headers)) {
      try {
        xhr.setRequestHeader(key, value);
      } catch {
        /* forbidden header — ignore */
      }
    }
    if (timeout && timeout > 0) xhr.timeout = timeout;
    if (xhr.upload) {
      xhr.upload.onprogress = (event: ProgressEvent) =>
        onUploadProgress({
          loaded: event.loaded,
          total: event.lengthComputable ? event.total : undefined,
        });
    }
    if (signal) {
      if (signal.aborted) xhr.abort();
      else signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.onload = () => {
      const parsed = parseXhrBody(xhr);
      const respHeaders = xhrResponseHeaders(xhr);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ data: parsed as T, status: xhr.status, headers: respHeaders });
      } else {
        reject(new HttpError(xhr.status, parsed, respHeaders));
      }
    };
    xhr.onerror = () => reject(new Error("Network Error"));
    xhr.ontimeout = () => {
      const error = new Error(`timeout of ${timeout}ms exceeded`) as Error & { code?: string };
      error.code = "ECONNABORTED";
      reject(error);
    };
    xhr.onabort = () => {
      const error = new Error("Request aborted") as Error & { name: string };
      error.name = "AbortError";
      reject(error);
    };
    xhr.send((body as XMLHttpRequestBodyInit) ?? null);
  });
}

// fetch-based transport that mimics the axios call used previously.
// On non-ok responses it throws HttpError(status, body, headers).
async function httpFetch<T>(config: HttpRequestConfig): Promise<HttpResponse<T>> {
  const { method = "GET", url, data, params, headers, timeout, signal } = config;

  // Append params as a query string if present
  let finalUrl = url;
  if (params && Object.keys(params).length > 0) {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) qs.append(key, String(value));
    }
    const query = qs.toString();
    if (query) finalUrl += (url.includes("?") ? "&" : "?") + query;
  }

  // Build headers/body (shared by both transports)
  const finalHeaders: Record<string, string> = { ...(headers || {}) };
  let body: BodyInit | undefined;
  if (
    data !== undefined &&
    data !== null &&
    method.toUpperCase() !== "GET" &&
    method.toUpperCase() !== "HEAD"
  ) {
    if (typeof FormData !== "undefined" && data instanceof FormData) {
      // Let the browser set the multipart Content-Type/boundary
      body = data;
    } else {
      body = JSON.stringify(data);
      if (!hasHeader(finalHeaders, "Content-Type")) {
        finalHeaders["Content-Type"] = "application/json";
      }
    }
  }

  // Upload progress requested → XHR (fetch can't report request-body progress).
  if (config.onUploadProgress && typeof XMLHttpRequest !== "undefined") {
    return xhrFetch<T>(method, finalUrl, finalHeaders, body, timeout, signal, config.onUploadProgress);
  }

  // Timeout + cancellation via AbortController
  const controller = new AbortController();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  let timedOut = false;
  const timeoutId =
    timeout && timeout > 0
      ? setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, timeout)
      : undefined;

  try {
    const res = await fetch(finalUrl, {
      method,
      headers: finalHeaders,
      body,
      signal: controller.signal,
    });

    if (!res.ok) {
      const errorBody = await parseResponseBody(res);
      throw new HttpError(res.status, errorBody, res.headers);
    }

    const parsed = await parseResponseBody(res);
    return { data: parsed as T, status: res.status, headers: res.headers };
  } catch (error) {
    // Normalize a timeout abort to an axios-style ECONNABORTED/timeout error
    if ((error as { name?: string })?.name === "AbortError" && timedOut) {
      const timeoutError = new Error(`timeout of ${timeout}ms exceeded`) as Error & {
        code?: string;
      };
      timeoutError.code = "ECONNABORTED";
      throw timeoutError;
    }
    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

// Error types for better classification
export interface ClassifiedError {
  type: "network" | "cors" | "auth" | "ratelimit" | "server" | "validation" | "general" | "unknown";
  message: string;
  retryable: boolean;
  retryAfter?: number;
  originalError?: any;
}

// Base API service with common functionality
export abstract class BaseApiService {
  protected cancelTokenSources: Map<string, AbortController> = new Map();
  protected pendingAbortControllers: AbortController[] = [];

  protected delay(ms: number, attempt: number = 0): Promise<void> {
    const backoffFactor = Math.pow(apiConfig.retryMultiplier, attempt);
    const finalDelay = Math.min(ms * backoffFactor, 30000); // Cap at 30 seconds
    return new Promise((resolve) => setTimeout(resolve, finalDelay));
  }

  protected classifyError(error: any): ClassifiedError {
    if (!error)
      return {
        type: "unknown",
        message: "Unknown error occurred",
        retryable: false,
        originalError: error,
      };

    // Network errors
    if (
      error.code === "ECONNABORTED" ||
      error.message?.includes("timeout") ||
      error.message?.includes("network error")
    ) {
      return {
        type: "network",
        message: "Connection issue. Please check your internet and try again.",
        retryable: true,
        originalError: error,
      };
    }

    // CORS errors
    if (
      error.message?.includes("CORS") ||
      error.message?.includes("cross-origin")
    ) {
      return {
        type: "cors",
        // Not "enable the CORS proxy": that control never existed in the UI, so the only
        // instruction this gave was impossible to follow. See api/index.ts fetchWithProxy.
        message: "Couldn't reach the capture service. Check your connection and try again — if it persists, the service may be temporarily unavailable.",
        retryable: false,
        originalError: error,
      };
    }

    const status = getStatus(error);

    // Auth errors
    if (status === 401 || status === 403) {
      return {
        type: "auth",
        message: "Authentication failed. Please restart the plugin.",
        retryable: false,
        originalError: error,
      };
    }

    // Rate limiting
    if (status === 429) {
      return {
        type: "ratelimit",
        message: "Rate limit exceeded. Please try again later.",
        retryable: true,
        retryAfter: parseInt(getHeader((error as HttpError).headers, "retry-after") || "5", 10),
        originalError: error,
      };
    }

    // Server errors
    if (status !== undefined && status >= 500) {
      return {
        type: "server",
        message: "Server error. The service may be experiencing issues.",
        retryable: true,
        originalError: error,
      };
    }

    // Validation errors
    if (status === 400 || status === 422) {
      return {
        type: "validation",
        message: (error as HttpError)?.data?.message || "The request contains invalid data.",
        retryable: false,
        originalError: error,
      };
    }

    return {
      type: "general",
      message: error.message || "An error occurred",
      retryable: false,
      originalError: error,
    };
  }

  protected async executeWithRetry<T>(
    operation: () => Promise<T>,
    context: string,
    maxRetries: number = apiConfig.maxRetries
  ): Promise<T> {
    let retryCount = 0;

    while (retryCount <= maxRetries) {
      try {
        return await operation();
      } catch (error) {
        const classifiedError = this.classifyError(error);

        const shouldRetry = classifiedError.retryable && retryCount < maxRetries;

        if (shouldRetry) {
          retryCount++;
          // Silent retry - no console output

          const retryDelayMs = classifiedError.retryAfter
            ? classifiedError.retryAfter * 1000
            : apiConfig.retryDelay;

          await this.delay(retryDelayMs, retryCount - 1);
        } else {
          throw new Error(`${context}: ${classifiedError.message}`);
        }
      }
    }

    throw new Error(`${context} failed after ${maxRetries} attempts`);
  }

  protected createCancelToken(key: string): AbortController {
    // Cancel any existing controller with this key
    this.cancelRequest(key);

    const controller = new AbortController();
    this.cancelTokenSources.set(key, controller);
    return controller;
  }

  protected cancelRequest(key: string): boolean {
    const controller = this.cancelTokenSources.get(key);
    if (controller) {
      controller.abort();
      this.cancelTokenSources.delete(key);
      return true;
    }
    return false;
  }

  public cancelAllRequests(): boolean {
    let cancelled = false;

    // Cancel in-flight requests tracked by key
    this.cancelTokenSources.forEach((controller) => {
      controller.abort();
      cancelled = true;
    });
    this.cancelTokenSources.clear();

    // Cancel fetch requests
    this.pendingAbortControllers.forEach((controller) => {
      try {
        controller.abort();
        cancelled = true;
      } catch {
        // Silent abort error - no console output
      }
    });
    this.pendingAbortControllers = [];

    return cancelled;
  }

  protected async makeRequest<T>(
    config: HttpRequestConfig,
    context: string,
    cancelKey?: string
  ): Promise<HttpResponse<T>> {
    return this.executeWithRetry(async () => {
      const requestConfig: HttpRequestConfig = {
        ...config,
        timeout: config.timeout || apiConfig.timeout.default,
      };

      if (cancelKey) {
        const controller = this.createCancelToken(cancelKey);
        requestConfig.signal = controller.signal;
      }

      const response = await httpFetch<T>(requestConfig);

      // Validate response
      if (!response.data) {
        throw new Error("Empty response received from server");
      }

      return response;
    }, context);
  }

  protected buildUrl(endpoint: string): string {
    return buildApiUrl(endpoint);
  }
}

// Health check service
export class HealthService extends BaseApiService {
  private lastHealthCheckTime: number = 0;
  /**
   * How long a HEALTHY result is reused before probing again.
   *
   * It was thirty minutes, and it cached failures too, which broke all three consumers at once:
   *
   *   - the offline banner's "Retry Connection" button called straight into here, hit the cache,
   *     and returned the stored "offline" without making a request. Clicking it did nothing, for
   *     half an hour, whether or not the API had recovered — and Import stays disabled the whole
   *     time, with no other way to force a re-check.
   *   - `useHealthQuery` has `refetchInterval: 5 * 60_000` and a comment reading "polled so the UI
   *     can surface an offline state". The poll ran; this returned the cached answer without
   *     touching the network, so it could not surface anything for up to thirty minutes.
   *   - the mount-time check in both capture components, same way.
   *
   * A minute, so it still absorbs a burst of calls on mount — which is what it is for — while
   * staying well inside the five-minute poll it exists to serve.
   */
  private healthCheckCacheMs: number = 60_000;
  private healthCheckPromise: Promise<boolean> | null = null;
  private connectionListeners: Array<(status: "online" | "offline" | "checking") => void> = [];
  private connectionStatus: "online" | "offline" | "checking" = "checking";

  onConnectionStatusChange(
    listener: (status: "online" | "offline" | "checking") => void
  ): () => void {
    this.connectionListeners.push(listener);
    listener(this.connectionStatus);
    return () => {
      this.connectionListeners = this.connectionListeners.filter(l => l !== listener);
    };
  }

  private setConnectionStatus(status: "online" | "offline" | "checking"): void {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      this.connectionListeners.forEach(listener => listener(status));
    }
  }

  async checkHealth(silent: boolean = false): Promise<boolean> {
    const now = Date.now();
    const timeSinceLastCheck = now - this.lastHealthCheckTime;

    // Reuse a recent HEALTHY result only. A failure is never cached: the next caller is either a
    // user who pressed Retry or a poll asking whether the service came back, and both need a real
    // request to answer. Caching "offline" made both of them no-ops.
    if (
      timeSinceLastCheck < this.healthCheckCacheMs &&
      this.connectionStatus === "online"
    ) {
      return true;
    }

    // If there's already a health check in progress, return that promise
    if (this.healthCheckPromise) {
      return this.healthCheckPromise;
    }

    if (!silent) {
      this.setConnectionStatus("checking");
    }

    this.healthCheckPromise = (async () => {
      try {
        const response = await this.makeRequest<{status: string}>({
          method: 'GET',
          url: this.buildUrl(apiConfig.endpoints.health),
          timeout: apiConfig.timeout.health
        }, "Health check");

        this.lastHealthCheckTime = Date.now();

        if (response.data && response.data.status === "healthy") {
          this.setConnectionStatus("online");
          return true;
        } else {
          this.setConnectionStatus("offline");
          return false;
        }
      } catch {
        this.lastHealthCheckTime = Date.now();
        this.setConnectionStatus("offline");
        // Silent health check failure - no console output
        return false;
      } finally {
        this.healthCheckPromise = null;
      }
    })();

    return this.healthCheckPromise;
  }
}

// Export singleton instances
export const healthService = new HealthService();